import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  useTheme,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  DirectionsBus,
  LocationOn,
  Speed,
  Timeline,
  Assessment,
  Settings,
} from "@mui/icons-material";
import axios from "axios";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import LineChart from "../../components/LineChart";
import {
  InfoItem,
  TripCard,
  MaintenanceView,
  StatCards,
  performanceData,
} from "./VehicleDetailsComponents";
import {
  defaultVehicle,
  defaultTrips,
  defaultPerformanceData,
} from "./defaultValues";
import PremiumFeature from "./PremiumFeature";
import { MAIN_URL } from "../../data/const";

const VehicleDetails = () => {
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [vehicle, setVehicle] = useState(defaultVehicle);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [trips, setTrips] = useState(
    Array.isArray(defaultTrips) ? defaultTrips : []
  );
  const [chartData, setChartData] = useState(defaultPerformanceData);

  useEffect(() => {
    fetchVehicleDetails();
  }, [id]);

  const fetchVehicleDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${MAIN_URL}/api/v1/vehicle/vehicle/${id}`
      );

      // Validate and set vehicle data with defaults
      const vehicleData = response.data.data || defaultVehicle;
      setVehicle({
        ...defaultVehicle,
        ...vehicleData,
      });
      console.log("data of fetch", response.data.data);

      // Fetch trips data
      try {
        const tripsResponse = await axios.get(
          `${MAIN_URL}/api/v1/vehicle/trips/${id}`
        );
        setTrips(tripsResponse.data || defaultTrips);
      } catch (tripError) {
        console.error("Error fetching trips:", tripError);
        setTrips(defaultTrips);
      }

      // Process performance data if available
      try {
        if (response.data && response.data.performanceMetrics) {
          const processedData = processPerformanceData(
            response.data.performanceMetrics
          );
          setChartData(processedData);
        } else {
          setChartData(defaultPerformanceData);
        }
      } catch (performanceError) {
        console.error("Error processing performance data:", performanceError);
        setChartData(defaultPerformanceData);
      }

      setError(null);
    } catch (error) {
      console.error("Error fetching vehicle details:", error);
      setError("Failed to load vehicle details. Showing default values.");
      setVehicle(defaultVehicle);
      setTrips(defaultTrips);
      setChartData(defaultPerformanceData);
    } finally {
      setLoading(false);
    }
  };

  const processPerformanceData = (metrics) => {
    try {
      // Add your data processing logic here
      return defaultPerformanceData;
    } catch (error) {
      console.error("Error processing performance data:", error);
      return defaultPerformanceData;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  console.log("Trips", trips);

  return (
    <Box m="20px">
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Header
        title={vehicle?.name || defaultVehicle.name}
        subtitle={vehicle?.vehicleNum || defaultVehicle.vehicleNum}
      />

      {/* Vehicle Overview */}
      <Card sx={{ mb: 4, backgroundColor: colors.primary[400] }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <img
                src={vehicle?.src || "/rd.png"}
                alt={vehicle?.name || "Vehicle"}
                style={{
                  width: "50%",
                  height: "auto",
                  borderRadius: "8px",
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h5" mb={2}>
                Vehicle Information
              </Typography>
              <Grid container spacing={2}>
                <InfoItem
                  icon={<DirectionsBus />}
                  label="Vehicle Type"
                  value={vehicle?.Type || defaultVehicle.Type}
                />
                <InfoItem
                  icon={<Speed />}
                  label="Status"
                  value={vehicle?.isVerified ? "Verified" : "Pending"}
                />
                <InfoItem
                  icon={<Timeline />}
                  label="Total Trips"
                  value={vehicle?.Trip?.length || 0}
                />
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabs Section */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(event, newValue) => setTabValue(newValue)}
          textColor="inherit"
          TabIndicatorProps={{
            style: { backgroundColor: "red" }, // Tab indicator color
          }}
        >
          <Tab label="Live Tracking" icon={<LocationOn />} />
          <Tab
            label={`Trip History (${trips.length || 0})`}
            icon={<Timeline />}
          />
          <Tab label="Analytics" icon={<Assessment />} />
          <Tab label="Maintenance" icon={<Settings />} />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box>
        {tabValue === 0 && (
          <Card sx={{ height: "500px", backgroundColor: colors.primary[400] }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Live Tracking
              </Typography>
              <Typography color="text.secondary">
                Map View Coming Soon
              </Typography>
            </CardContent>
          </Card>
        )}

        {tabValue === 1 && (
          <Grid container spacing={3}>
            {/* // Note fix replace defaultTrips by actual trips */}
            {defaultTrips.length > 0 ? (
              defaultTrips.map((trip) => (
                <Grid item xs={12} key={trip._id || Math.random()}>
                  <TripCard trip={trip} colors={colors} />
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: colors.primary[400], p: 3 }}>
                  <Typography align="center" color="text.secondary">
                    No trips recorded yet
                  </Typography>
                </Card>
              </Grid>
            )}
          </Grid>
        )}

        {/* {tabValue === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Card sx={{ p: 3, backgroundColor: colors.primary[400] }}>
                <Typography variant="h6" mb={2}>
                  Performance Metrics
                </Typography>
                <Box height="400px">
                  <LineChart data={chartData} />
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} lg={4}>
              <StatCards data={vehicle} colors={colors} />
            </Grid>
          </Grid>
        )} */}

        {tabValue === 2 && <PremiumFeature />}

        {/* <MaintenanceView vehicle={vehicle} colors={colors} />   ADD THIS IN BELOW TAB VALUE*/}
        {tabValue === 3 && <PremiumFeature />}
      </Box>
    </Box>
  );
};

export default VehicleDetails;
